/**
 * Agent Knowledge Detail Page.
 *
 * Shows source metadata, knowledge, ingestion batches, gaps, interviews,
 * and transcript history.
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Helmet } from 'react-helmet';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  AlertCircle,
  ArrowLeft,
  Archive,
  CheckCircle2,
  ChevronRight,
  Copy,
  ExternalLink,
  FlaskConical,
  GraduationCap,
  LayoutList,
  Loader2,
  MessageSquare,
  Plus,
  RotateCcw,
  Trash2,
  Upload,
  User,
  Users,
} from 'lucide-react';

import { PageHeader } from '@/components/layout/PageHeader';
import { Markdown } from '@/components/common/Markdown';
import { DataSurface } from '@/components/platform/DataSurface';
import { MetricStrip } from '@/components/platform/MetricStrip';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { HelpTooltip } from '@/components/ui/HelpTooltip';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useToast } from '@/components/ui/use-toast';
import {
  ContentSelector,
  KnowledgeAssetSelector,
} from '@/components/onboarding/KnowledgeSourceSelectors';
import { KnowledgeRemovalDialog } from '@/components/onboarding/KnowledgeRemovalDialog';
import { ActiveGapsTab } from '@/components/knowledge-gaps/ActiveGapsTab';
import { TriggerPipelineTab } from '@/components/knowledge-gaps/TriggerPipelineTab';
import { RunHistoryTab } from '@/components/knowledge-gaps/RunHistoryTab';
import { getFrequencyMeta } from '@/components/knowledge-gaps/active-gaps/helpers';
import { useInterviewerSetup } from '@/hooks/useInterviewerSetup';
import { useKnowledgeAssets } from '@/hooks/useKnowledgeAssets';
import contentLibraryService from '@/lib/api/contentLibrary';
import knowledgeGapsService, { type KGSummaryResponse } from '@/lib/api/knowledgeGaps';
import onboardingService from '@/lib/api/onboarding';
import type {
  FileUploadInfo,
  InterviewEntry,
  InterviewerDetail,
  InterviewerPromptImpactSummary,
  RemoveInterviewerKnowledgePayload,
  InterviewerSessionSummary,
  SessionDetailResponse,
} from '@/lib/api/onboarding';
import { cn } from '@/lib/utils';

const getPhaseLabel = (phase: string) => {
  switch (phase) {
    case 'processing':
      return { label: 'Processing', variant: 'secondary' as const };
    case 'planning':
      return { label: 'Planning', variant: 'secondary' as const };
    case 'interviewing':
      return { label: 'In Progress', variant: 'secondary' as const };
    case 'reviewing':
      return { label: 'Reviewing', variant: 'secondary' as const };
    case 'completed':
      return { label: 'Ready', variant: 'default' as const };
    case 'failed':
      return { label: 'Failed', variant: 'destructive' as const };
    default:
      return { label: phase, variant: 'secondary' as const };
  }
};

const formatBatchStatus = (status: string) => status.replace(/_/g, ' ');
const normalizeSourceKey = (value: string) =>
  value.trim().replace(/\/+$/, '').toLowerCase();

const getBatchStatusLabel = (status: string) => {
  switch (status) {
    case 'merged':
      return 'Updated';
    case 'completed':
      return 'Ready';
    case 'processing':
      return 'Processing';
    case 'pending':
      return 'Queued';
    case 'failed':
      return 'Failed';
    default:
      return formatBatchStatus(status);
  }
};

const formatPromptDomain = (domain: string) =>
  domain
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (char) => char.toUpperCase());

type DisplaySource = {
  key: string;
  type: string;
  label: string;
  status: string;
  sourceKey: 'website' | 'knowledge_base' | 'knowledge_asset' | 'content_video';
  knowledgeAssetId?: string;
  videoId?: string;
};

const buildDisplaySources = ({
  interviewer,
  orgAssets,
  contentVideos,
}: {
  interviewer: InterviewerDetail;
  orgAssets: Array<{
    id: string;
    asset_type: string;
    title: string | null;
    filename: string | null;
    url: string | null;
    processing_status: string;
  }>;
  contentVideos: Array<{
    video_id: string;
    title?: string;
    filename: string;
    status: string;
  }>;
}) => {
  const assetIds = interviewer.knowledge_asset_ids ?? [];
  const videoIds = interviewer.selected_video_ids ?? [];

  const rawSources: DisplaySource[] = [
    ...orgAssets
      .filter((asset) => assetIds.includes(asset.id))
      .map((asset) => ({
        key: asset.id,
        type: asset.asset_type,
        label: asset.title || asset.filename || asset.url || asset.asset_type,
        status: asset.processing_status,
        sourceKey: 'knowledge_asset' as const,
        knowledgeAssetId: asset.id,
      })),
    ...contentVideos
      .filter((video) => videoIds.includes(video.video_id))
      .map((video) => ({
        key: video.video_id,
        type: 'content_video',
        label: video.title || video.filename,
        status: video.status,
        sourceKey: 'content_video' as const,
        videoId: video.video_id,
      })),
    ...(interviewer.website_url
      ? [{
          key: 'website',
          type: 'url',
          label: interviewer.website_url,
          status: 'completed',
          sourceKey: 'website' as const,
        }]
      : []),
    ...(interviewer.knowledge_base_url
      ? [{
          key: 'knowledge-base',
          type: 'url',
          label: interviewer.knowledge_base_url,
          status: 'completed',
          sourceKey: 'knowledge_base' as const,
        }]
      : []),
  ];

  const deduped = new Map<string, (typeof rawSources)[number]>();
  rawSources.forEach((source) => {
    const dedupeKey =
      source.type === 'url'
        ? `url:${normalizeSourceKey(source.label)}`
        : `${source.type}:${normalizeSourceKey(source.label)}`;
    if (!deduped.has(dedupeKey)) {
      deduped.set(dedupeKey, source);
    }
  });

  return Array.from(deduped.values());
};

const buildRemovalPayload = (source: DisplaySource): RemoveInterviewerKnowledgePayload => {
  switch (source.sourceKey) {
    case 'website':
      return { remove_website: true };
    case 'knowledge_base':
      return { remove_knowledge_base: true };
    case 'content_video':
      return source.videoId ? { selected_video_ids: [source.videoId] } : {};
    case 'knowledge_asset':
      return source.knowledgeAssetId ? { knowledge_asset_ids: [source.knowledgeAssetId] } : {};
    default:
      return {};
  }
};

export const AttachKnowledgeDialog: React.FC<{
  interviewer: InterviewerDetail;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}> = ({ interviewer, open, onOpenChange }) => {
  const { ingestKnowledge, isIngesting, uploadFile } = useInterviewerSetup(interviewer.organization_id);
  const [websiteUrl, setWebsiteUrl] = useState('');
  const [knowledgeBaseUrl, setKnowledgeBaseUrl] = useState('');
  const [selectedAssetIds, setSelectedAssetIds] = useState<string[]>([]);
  const [selectedVideoIds, setSelectedVideoIds] = useState<string[]>([]);
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [isUploadingFiles, setIsUploadingFiles] = useState(false);

  const resetForm = useCallback(() => {
    setWebsiteUrl('');
    setKnowledgeBaseUrl('');
    setSelectedAssetIds([]);
    setSelectedVideoIds([]);
    setSelectedFiles([]);
  }, []);

  const isBusy = isIngesting || isUploadingFiles;
  const hasChanges =
    !!websiteUrl.trim() ||
    !!knowledgeBaseUrl.trim() ||
    selectedAssetIds.length > 0 ||
    selectedVideoIds.length > 0 ||
    selectedFiles.length > 0;

  const handleOpenChange = (nextOpen: boolean) => {
    if (isBusy) return;
    if (!nextOpen) resetForm();
    onOpenChange(nextOpen);
  };

  const handleSubmit = async () => {
    if (!hasChanges || isBusy) return;

    setIsUploadingFiles(true);

    let fileUploads: FileUploadInfo[] | undefined;
    if (selectedFiles.length > 0) {
      const uploaded = await Promise.all(selectedFiles.map((file) => uploadFile(file)));
      if (uploaded.some((file) => !file)) {
        setIsUploadingFiles(false);
        return;
      }
      fileUploads = uploaded.filter(Boolean) as FileUploadInfo[];
    }

    setIsUploadingFiles(false);

    const result = await ingestKnowledge(interviewer.id, {
      websiteUrl: websiteUrl.trim() || undefined,
      knowledgeBaseUrl: knowledgeBaseUrl.trim() || undefined,
      fileUploads,
      selectedVideoIds,
      knowledgeAssetIds: selectedAssetIds,
    });

    if (result) {
      resetForm();
      onOpenChange(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-h-[90vh] max-w-3xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Add Sources</DialogTitle>
          <DialogDescription>
            Add files, URLs, or library items. Hobbes will read them and update Agent Knowledge.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-5">
          <div className="grid gap-4 md:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="attach-website-url">Website URL</Label>
              <Input
                id="attach-website-url"
                type="url"
                placeholder="https://example.com"
                value={websiteUrl}
                onChange={(event) => setWebsiteUrl(event.target.value)}
                disabled={isBusy}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="attach-knowledge-base-url">Documentation URL</Label>
              <Input
                id="attach-knowledge-base-url"
                type="url"
                placeholder="https://docs.example.com"
                value={knowledgeBaseUrl}
                onChange={(event) => setKnowledgeBaseUrl(event.target.value)}
                disabled={isBusy}
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="attach-files">Upload Files</Label>
            <Input
              id="attach-files"
              type="file"
              multiple
              accept=".pdf,video/*"
              disabled={isBusy}
              onChange={(event) => setSelectedFiles(Array.from(event.target.files ?? []))}
            />
            {selectedFiles.length > 0 && (
              <div className="flex flex-wrap gap-2 pt-1">
                {selectedFiles.map((file) => (
                  <span
                    key={`${file.name}-${file.size}`}
                    className="rounded-md border bg-muted/50 px-2.5 py-1 text-xs"
                  >
                    {file.name}
                  </span>
                ))}
              </div>
            )}
          </div>

          <KnowledgeAssetSelector
            orgId={interviewer.organization_id}
            selectedAssetIds={selectedAssetIds}
            onSelectionChange={setSelectedAssetIds}
            disabled={isBusy}
            excludedAssetIds={interviewer.knowledge_asset_ids ?? []}
          />

          <ContentSelector
            orgId={interviewer.organization_id}
            selectedVideoIds={selectedVideoIds}
            onSelectionChange={setSelectedVideoIds}
            disabled={isBusy}
            excludedVideoIds={interviewer.selected_video_ids ?? []}
          />
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => handleOpenChange(false)} disabled={isBusy}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={!hasChanges || isBusy}>
            {isBusy ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Adding...
              </>
            ) : (
              <>
                <Upload className="mr-2 h-4 w-4" />
                Add Sources
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

type OverviewAction = {
  label: string;
  icon: React.ComponentType<{ className?: string }>;
  onClick: () => void;
};

const pluralize = (count: number, singular: string, plural = `${singular}s`) =>
  `${count} ${count === 1 ? singular : plural}`;

const getAgentStateLabel = (interviewer: InterviewerDetail) => {
  if (interviewer.processing_status === 'failed' || interviewer.config_generation_status === 'failed') {
    return 'Needs attention';
  }
  if (interviewer.config_generation_status === 'generating' || interviewer.prompt_status === 'regenerating') {
    return 'Updating';
  }
  if (interviewer.prompt_status === 'stale') {
    return 'Update available';
  }
  return 'Up to date';
};

const SectionHeading: React.FC<{
  title: string;
  description?: string;
  action?: React.ReactNode;
}> = ({ title, description, action }) => (
  <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
    <h3 className="inline-flex items-center text-[13px] font-medium text-foreground">
      {title}
      {description ? <HelpTooltip text={description} /> : null}
    </h3>
    {action ? <div className="shrink-0">{action}</div> : null}
  </div>
);

const AttentionRow: React.FC<{
  title: string;
  description: string;
  action: OverviewAction;
  tone?: 'default' | 'warning' | 'danger' | 'success';
  meta?: React.ReactNode;
}> = ({ title, description, action, tone = 'default', meta }) => {
  const ActionIcon = action.icon;
  const indicatorClassName =
    tone === 'danger'
      ? 'bg-destructive'
      : tone === 'warning'
        ? 'bg-amber-500'
        : tone === 'success'
          ? 'bg-emerald-500'
          : 'bg-primary';

  return (
    <button
      type="button"
      onClick={action.onClick}
      className="group grid w-full gap-3 px-4 py-3 text-left transition-colors hover:bg-muted/35 sm:grid-cols-[minmax(0,1fr)_auto]"
    >
      <div className="min-w-0">
        <div className="flex min-w-0 items-center gap-2">
          <span className={cn('h-2 w-2 shrink-0 rounded-full', indicatorClassName)} />
          <p className="truncate text-sm font-medium text-foreground">{title}</p>
        </div>
        <p className="mt-1 line-clamp-2 text-sm text-muted-foreground">{description}</p>
      </div>
      <div className="flex items-center justify-between gap-3 sm:justify-end">
        {meta ? <div className="shrink-0">{meta}</div> : null}
        <span className="inline-flex items-center text-xs font-medium text-muted-foreground group-hover:text-foreground">
          <ActionIcon className="mr-1.5 h-3.5 w-3.5" />
          {action.label}
          <ChevronRight className="ml-1 h-3.5 w-3.5" />
        </span>
      </div>
    </button>
  );
};

export const OverviewTab: React.FC<{
  interviewer: InterviewerDetail;
  interviewerId: string;
  activeGapCount?: number;
  onGenerationStarted?: () => void;
}> = ({
  interviewer,
  interviewerId,
  activeGapCount = 0,
  onGenerationStarted,
}) => {
  const [showAttachDialog, setShowAttachDialog] = useState(false);
  const { assets: orgAssets } = useKnowledgeAssets(interviewer.organization_id);
  const { data: videosData } = useQuery({
    queryKey: ['content-library-videos', interviewer.organization_id],
    queryFn: async () => {
      const response = await contentLibraryService.getOrganizationVideos(interviewer.organization_id);
      if (!response.success || !response.data) {
        throw new Error(response.error || 'Failed to fetch videos');
      }
      return response.data;
    },
    enabled: !!interviewer.organization_id && (interviewer.selected_video_ids?.length ?? 0) > 0,
  });

  const displaySources = useMemo(
    () => {
      const matchedAssets = (interviewer.knowledge_asset_ids ?? []).length > 0
        ? orgAssets.filter((asset) => (interviewer.knowledge_asset_ids ?? []).includes(asset.id))
        : [];
      const matchedVideos = (interviewer.selected_video_ids ?? []).length > 0
        ? (videosData?.videos ?? []).filter((video) =>
            (interviewer.selected_video_ids ?? []).includes(video.video_id)
          )
        : [];

      return buildDisplaySources({
        interviewer,
        orgAssets: matchedAssets,
        contentVideos: matchedVideos,
      });
    },
    [
      interviewer,
      orgAssets,
      videosData?.videos,
    ]
  );
  const latestBatch = (interviewer.ingestion_batches ?? [])[0] ?? null;
  const confirmedTopics = Object.entries(
    interviewer.knowledge_profile?.confirmed_topic_summaries ?? {}
  ).sort((left, right) => {
    const leftName = String(left[1]?.topic_name || left[0]);
    const rightName = String(right[1]?.topic_name || right[0]);
    return leftName.localeCompare(rightName);
  });
  const hasSources = displaySources.length > 0;
  const failedSources = displaySources.filter((source) => source.status === 'failed');
  const isKnowledgeProcessing =
    interviewer.processing_status === 'processing' ||
    interviewer.processing_status === 'planning' ||
    (!!latestBatch && !['completed', 'failed', 'merged'].includes(latestBatch.status));
  const needsAgentUpdate = interviewer.prompt_status === 'stale';
  const isUpdatingAgent =
    interviewer.config_generation_status === 'generating' ||
    interviewer.prompt_status === 'regenerating';
  const hasAgentUpdateIssue =
    interviewer.processing_status === 'failed' ||
    interviewer.config_generation_status === 'failed';
  const hasSourceIssue = hasAgentUpdateIssue || failedSources.length > 0;
  const promptImpactSummary: InterviewerPromptImpactSummary | null =
    interviewer.prompt_impact_summary ?? latestBatch?.prompt_impact ?? null;
  const shouldShowAgentStatusPanel = needsAgentUpdate || isUpdatingAgent || hasAgentUpdateIssue;
  const navigate = useNavigate();

  const { data: priorityGapsData } = useQuery({
    queryKey: ['knowledge-gaps-overview-priority', interviewer.organization_id],
    queryFn: async () => {
      const response = await knowledgeGapsService.getSummaries(interviewer.organization_id, {
        status: 'active',
        sort_by: 'occurrence_count_desc',
        page: 1,
        limit: 3,
        include_evidence: false,
      });
      if (!response.success || !response.data) {
        throw new Error(response.error || 'Failed to load priority gaps');
      }
      return response.data;
    },
    enabled: !!interviewer.organization_id && activeGapCount > 0,
    staleTime: 30_000,
  });

  const priorityGaps: KGSummaryResponse[] = priorityGapsData?.summaries ?? [];

  const reviewGapsAction: OverviewAction = {
    label: 'Review Gaps',
    icon: LayoutList,
    onClick: () => navigate('/agent-knowledge?tab=gaps'),
  };
  const viewSourcesAction: OverviewAction = {
    label: 'View Sources',
    icon: Archive,
    onClick: () => navigate('/agent-knowledge?tab=sources'),
  };
  const addSourcesAction: OverviewAction = {
    label: 'Add Sources',
    icon: Plus,
    onClick: () => setShowAttachDialog(true),
  };
  const reviewUpdateAction: OverviewAction = {
    label: 'Update Agent',
    icon: RotateCcw,
    onClick: () => document.getElementById('agent-status')?.scrollIntoView({ behavior: 'smooth', block: 'start' }),
  };
  const runSimulationAction: OverviewAction = {
    label: 'Run Simulation',
    icon: FlaskConical,
    onClick: () => navigate('/evaluation/simulations'),
  };

  const sourceStateLabel = hasSourceIssue
    ? 'Needs attention'
    : isKnowledgeProcessing
      ? 'Processing'
      : hasSources
        ? undefined
        : 'No sources yet';

  const gapAttentionItems = activeGapCount > 0
    ? priorityGaps.length > 0
      ? priorityGaps.map((gap) => {
          const frequency = getFrequencyMeta(gap.occurrence_count);
          return {
            key: `gap-${gap.id}`,
            title: gap.topic,
            description: gap.summary || gap.suggested_action || 'Review this gap and decide what the agent should know.',
            action: reviewGapsAction,
            tone: 'warning' as const,
            meta: (
              <Badge variant="outline" className={cn('whitespace-nowrap text-[10px]', frequency.className)}>
                {frequency.label}
              </Badge>
            ),
          };
        })
      : [{
          key: 'open-gaps',
          title: 'Review open gaps',
          description: 'Start with the gaps that appear most often in evaluations.',
          action: reviewGapsAction,
          tone: 'warning' as const,
          meta: <Badge variant="outline">{activeGapCount} open</Badge>,
        }]
    : [];

  const attentionItems = [
    hasSourceIssue
      ? {
          key: 'source-issue',
          title: 'Review source health',
          description:
            failedSources.length > 0
              ? `${pluralize(failedSources.length, 'source')} ${failedSources.length === 1 ? 'needs' : 'need'} attention.`
              : 'A source or agent update did not finish cleanly.',
          action: viewSourcesAction,
          tone: 'danger' as const,
        }
      : null,
    ...gapAttentionItems,
    needsAgentUpdate
      ? {
          key: 'agent-update',
          title: 'Review the agent update',
          description: promptImpactSummary?.summary || 'Agent Knowledge has changed since the live agent was last updated.',
          action: reviewUpdateAction,
          tone: 'default' as const,
        }
      : null,
    isKnowledgeProcessing
      ? {
          key: 'source-processing',
          title: 'Check source processing',
          description: 'Hobbes is reading source material and updating Agent Knowledge.',
          action: viewSourcesAction,
          tone: 'default' as const,
        }
      : null,
    !hasSources
      ? {
          key: 'no-sources',
          title: 'Add sources to start Agent Knowledge',
          description: 'Attach docs, URLs, or library items so the agent has reliable knowledge to use.',
          action: addSourcesAction,
          tone: 'default' as const,
        }
      : null,
  ].filter(Boolean).slice(0, 4) as Array<{
    key: string;
    title: string;
    description: string;
    action: OverviewAction;
    tone: 'default' | 'warning' | 'danger' | 'success';
    meta?: React.ReactNode;
  }>;

  if (attentionItems.length === 0) {
    attentionItems.push({
      key: 'no-action',
      title: 'No action needed',
      description: 'Agent Knowledge is ready. Run a simulation when you want a fresh quality check.',
      action: runSimulationAction,
      tone: 'success',
    });
  }

  return (
    <div className="space-y-5">
      <MetricStrip
        items={[
          {
            label: 'Sources',
            value: displaySources.length,
            meta: sourceStateLabel,
          },
          {
            label: 'Topics',
            value: confirmedTopics.length,
            meta: confirmedTopics.length === 0 ? 'None yet' : undefined,
          },
          {
            label: 'Gaps',
            value: activeGapCount,
            meta: activeGapCount > 0 ? 'Open' : 'Clear',
          },
          {
            label: 'Agent',
            value: getAgentStateLabel(interviewer),
            meta: needsAgentUpdate ? 'Update available' : undefined,
          },
        ]}
      />

      <section className="space-y-3">
        <SectionHeading
          title="Needs attention"
          description="The highest-priority work for keeping Agent Knowledge clean."
        />
        <DataSurface>
          <div className="divide-y">
            {attentionItems.map((item) => (
              <AttentionRow
                key={item.key}
                title={item.title}
                description={item.description}
                action={item.action}
                tone={item.tone}
                meta={item.meta}
              />
            ))}
          </div>
        </DataSurface>
      </section>

      {interviewer.processing_status === 'completed' && shouldShowAgentStatusPanel && (
        <ConfigSection
          interviewer={interviewer}
          interviewerId={interviewerId}
          onGenerationStarted={onGenerationStarted}
        />
      )}

      <AttachKnowledgeDialog
        interviewer={interviewer}
        open={showAttachDialog}
        onOpenChange={setShowAttachDialog}
      />
    </div>
  );
};

export const SourcesTab: React.FC<{
  interviewer: InterviewerDetail;
  interviewerId: string;
  onAddSources: () => void;
}> = ({ interviewer, interviewerId, onAddSources }) => {
  const queryClient = useQueryClient();
  const [sourceToRemove, setSourceToRemove] = useState<DisplaySource | null>(null);
  const { assets: orgAssets } = useKnowledgeAssets(interviewer.organization_id);
  const { data: videosData } = useQuery({
    queryKey: ['content-library-videos', interviewer.organization_id],
    queryFn: async () => {
      const response = await contentLibraryService.getOrganizationVideos(interviewer.organization_id);
      if (!response.success || !response.data) {
        throw new Error(response.error || 'Failed to fetch videos');
      }
      return response.data;
    },
    enabled: !!interviewer.organization_id && (interviewer.selected_video_ids?.length ?? 0) > 0,
  });

  const displaySources = useMemo(() => {
    const matchedAssets = (interviewer.knowledge_asset_ids ?? []).length > 0
      ? orgAssets.filter((asset) => (interviewer.knowledge_asset_ids ?? []).includes(asset.id))
      : [];
    const matchedVideos = (interviewer.selected_video_ids ?? []).length > 0
      ? (videosData?.videos ?? []).filter((video) =>
          (interviewer.selected_video_ids ?? []).includes(video.video_id)
        )
      : [];

    return buildDisplaySources({
      interviewer,
      orgAssets: matchedAssets,
      contentVideos: matchedVideos,
    });
  }, [interviewer, orgAssets, videosData?.videos]);

  const handleRemoved = async () => {
    await queryClient.invalidateQueries({ queryKey: ['interviewer', interviewerId] });
    await queryClient.invalidateQueries({ queryKey: ['interviewers', interviewer.organization_id] });
    await queryClient.invalidateQueries({ queryKey: ['knowledge-gaps-report', interviewer.organization_id] });
  };

  return (
    <>
      {displaySources.length > 0 ? (
        <DataSurface>
          <div className="grid grid-cols-[minmax(0,1fr)_120px_120px_120px_48px] gap-3 border-b bg-muted/30 px-4 py-2 text-xs font-medium uppercase tracking-wide text-muted-foreground">
            <span className="flex items-center gap-1.5">
              Source
              <HelpTooltip
                text="Sources feeding this agent. Source Library remains the reusable org-wide source pool."
                side="right"
                className="ml-0 h-3 w-3"
              />
            </span>
            <span>Type</span>
            <span>Status</span>
            <span>Used by agent</span>
            <span className="sr-only">Actions</span>
          </div>
          <div className="divide-y">
            {displaySources.map((source) => (
              <div
                key={`${source.sourceKey}:${source.key}`}
                className="grid grid-cols-[minmax(0,1fr)_120px_120px_120px_48px] items-center gap-3 px-4 py-3"
              >
                <div className="min-w-0">
                  <p className="truncate text-sm font-medium">{source.label}</p>
                </div>
                <p className="text-sm text-muted-foreground">
                  {source.type === 'url' ? 'URL' : formatPromptDomain(source.type)}
                </p>
                <Badge variant="outline" className="w-fit text-[10px]">
                  {getBatchStatusLabel(source.status)}
                </Badge>
                <p className="text-sm text-muted-foreground">Yes</p>
                <Button
                  size="icon"
                  variant="ghost"
                  className="h-8 w-8 text-muted-foreground hover:text-destructive"
                  onClick={() => setSourceToRemove(source)}
                  disabled={interviewer.processing_status !== 'completed'}
                  aria-label={`Remove ${source.label}`}
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </Button>
              </div>
            ))}
          </div>
        </DataSurface>
      ) : (() => {
        const latestBatch = interviewer.ingestion_batches?.[0];
        const isAttaching =
          interviewer.processing_status === 'processing' ||
          interviewer.processing_status === 'planning' ||
          (!!latestBatch && !['completed', 'failed'].includes(latestBatch.status));

        if (isAttaching) {
          return (
            <DataSurface className="border-dashed">
              <div className="px-4 py-10 text-center">
                <div className="mx-auto flex h-9 w-9 items-center justify-center rounded-full bg-primary/10">
                  <Loader2 className="h-4 w-4 animate-spin text-primary" />
                </div>
                <p className="mt-3 text-sm font-medium">Attaching sources…</p>
                <p className="mx-auto mt-2 max-w-md text-sm text-muted-foreground">
                  Hobbes is ingesting the sources you just added. Sources will appear here as soon as processing completes — usually within a minute or two.
                </p>
              </div>
            </DataSurface>
          );
        }

        return (
          <DataSurface className="border-dashed">
            <div className="px-4 py-10 text-center">
              <p className="text-sm font-medium">No sources attached yet</p>
              <p className="mx-auto mt-2 max-w-md text-sm text-muted-foreground">
                Attach sources from Source Library, add a URL, or upload files so Agent Knowledge can learn from them.
              </p>
              <div className="mt-4 flex justify-center">
                <Button size="sm" onClick={onAddSources}>
                  <Plus className="mr-1.5 h-3.5 w-3.5" />
                  Add Sources
                </Button>
              </div>
            </div>
          </DataSurface>
        );
      })()}

      <KnowledgeRemovalDialog
        organizationId={interviewer.organization_id}
        interviewerId={interviewer.id}
        sourceLabel={sourceToRemove?.label || 'this source'}
        payload={sourceToRemove ? buildRemovalPayload(sourceToRemove) : null}
        open={!!sourceToRemove}
        onOpenChange={(open) => {
          if (!open) setSourceToRemove(null);
        }}
        description={
          <>
            Remove <span className="font-medium text-foreground">{sourceToRemove?.label || 'this source'}</span> from
            this agent. I&apos;ll refresh Agent Knowledge from the remaining sources after you confirm.
          </>
        }
        onRemoved={handleRemoved}
      />
    </>
  );
};

export const KnowledgeTab: React.FC<{
  interviewer: InterviewerDetail;
  activeGapCount?: number;
}> = ({ interviewer, activeGapCount = 0 }) => {
  const navigate = useNavigate();
  const [selectedTopicId, setSelectedTopicId] = useState<string | null>(null);

  // Separate canonical topics from synthetic/follow-up topics (defensive for old data)
  const confirmedTopics = useMemo(() => {
    const allTopicEntries = Object.entries(
      interviewer.knowledge_profile?.confirmed_topic_summaries ?? {}
    );

    const canonical = allTopicEntries
      .filter(([topicId]) => !topicId.startsWith('open_question_') && topicId !== 'general_followup')
      .sort((left, right) => {
        const leftName = String(left[1]?.topic_name || left[0]);
        const rightName = String(right[1]?.topic_name || right[0]);
        return leftName.localeCompare(rightName);
      });

    const synthetic = allTopicEntries
      .filter(([topicId]) => topicId.startsWith('open_question_') || topicId === 'general_followup')
      .sort((left, right) => {
        const leftName = String(left[1]?.topic_name || left[0]);
        const rightName = String(right[1]?.topic_name || right[0]);
        return leftName.localeCompare(rightName);
      });

    // Show canonical topics first, then any remaining synthetic/follow-up topics
    return [...canonical, ...synthetic];
  }, [interviewer.knowledge_profile?.confirmed_topic_summaries]);
  const selectedTopic =
    confirmedTopics.find(([topicId]) => topicId === selectedTopicId) ?? confirmedTopics[0] ?? null;

  useEffect(() => {
    if (confirmedTopics.length === 0) {
      setSelectedTopicId(null);
      return;
    }
    if (!selectedTopicId || !confirmedTopics.some(([topicId]) => topicId === selectedTopicId)) {
      setSelectedTopicId(confirmedTopics[0][0]);
    }
  }, [confirmedTopics, selectedTopicId]);

  return (
    <div className="space-y-5">
      {activeGapCount > 0 && (
        <DataSurface>
          <button
            type="button"
            onClick={() => navigate('/agent-knowledge?tab=gaps')}
            className="flex w-full items-center justify-between gap-3 px-4 py-3 text-left transition-colors hover:bg-muted/35"
          >
            <div className="min-w-0">
              <p className="text-sm font-medium text-foreground">
                {pluralize(activeGapCount, 'open gap')} may affect this knowledge
              </p>
              <p className="mt-0.5 text-sm text-muted-foreground">
                Review unresolved gaps before treating these topics as complete.
              </p>
            </div>
            <span className="inline-flex shrink-0 items-center text-xs font-medium text-muted-foreground">
              Review Gaps
              <ChevronRight className="ml-1 h-3.5 w-3.5" />
            </span>
          </button>
        </DataSurface>
      )}

      {confirmedTopics.length > 0 ? (
        <DataSurface className="min-h-[420px]">
          <div className="grid min-h-[420px] lg:grid-cols-[280px_minmax(0,1fr)]">
            <aside className="border-b bg-muted/15 lg:border-b-0 lg:border-r">
              <div className="flex items-center justify-between gap-3 border-b px-4 py-3">
                <div className="flex items-center">
                  <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Topics</p>
                  <HelpTooltip
                    text="Confirmed guidance Hobbes can use when updating the agent."
                    side="right"
                  />
                </div>
                <Badge variant="outline">{confirmedTopics.length}</Badge>
              </div>
              <div className="divide-y">
                {confirmedTopics.map(([topicId, topicData]) => {
                  const topicName = topicData.topic_name || topicId;
                  const isActive = selectedTopic?.[0] === topicId;

                  return (
                    <button
                      key={topicId}
                      type="button"
                      onClick={() => setSelectedTopicId(topicId)}
                      className={cn(
                        'flex w-full items-center justify-between gap-3 px-4 py-3 text-left transition-colors',
                        isActive ? 'bg-background' : 'hover:bg-background/60'
                      )}
                    >
                      <span className="min-w-0 truncate text-sm font-medium text-foreground">
                        {topicName}
                      </span>
                      {isActive ? <ChevronRight className="h-3.5 w-3.5 text-muted-foreground" /> : null}
                    </button>
                  );
                })}
              </div>
            </aside>

            <div className="px-5 py-5">
              {selectedTopic ? (
                <>
                  <div className="border-b pb-4">
                    <p className="text-lg font-semibold text-foreground">
                      {selectedTopic[1].topic_name || selectedTopic[0]}
                    </p>
                  </div>

                  <div className="prose prose-sm mt-5 max-w-3xl text-foreground dark:prose-invert">
                    {typeof selectedTopic[1].summary === 'string' && selectedTopic[1].summary.trim() ? (
                      <Markdown content={selectedTopic[1].summary.trim()} />
                    ) : (
                      <p className="text-sm text-muted-foreground">
                        This topic does not have a narrative summary yet.
                      </p>
                    )}
                  </div>
                </>
              ) : null}
            </div>
          </div>
        </DataSurface>
      ) : (
        <DataSurface className="border-dashed">
          <div className="px-4 py-10 text-center">
            <p className="text-sm font-medium">No topics yet</p>
            <p className="mx-auto mt-2 max-w-md text-sm text-muted-foreground">
              Add sources or complete interviews so Hobbes can create durable guidance for this agent.
            </p>
          </div>
        </DataSurface>
      )}
    </div>
  );
};

const ConfigSection: React.FC<{
  interviewer: InterviewerDetail;
  interviewerId: string;
  onGenerationStarted?: () => void;
}> = ({ interviewer, interviewerId, onGenerationStarted }) => {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const configStatus = interviewer.config_generation_status;
  const promptStatus = interviewer.prompt_status;
  const promptImpactSummary = interviewer.prompt_impact_summary;
  const isApplyingKnowledge = configStatus === 'generating' || promptStatus === 'regenerating';
  const needsPromptRefresh = promptStatus === 'stale';
  const canGenerate = !!interviewer.website_url;

  const generateMutation = useMutation({
    mutationFn: async () => {
      const response = await onboardingService.generateConfig(
        interviewer.organization_id,
        interviewer.website_url || ''
      );
      if (!response.success) {
        throw new Error(response.error || 'Failed to start config generation');
      }
      return response.data;
    },
    onSuccess: () => {
      onGenerationStarted?.();
      queryClient.invalidateQueries({ queryKey: ['interviewer', interviewerId] });
      toast({ title: 'Agent update started', description: 'This may take a few minutes.' });
    },
    onError: () => {
      toast({
        variant: 'destructive',
        title: 'Agent update failed',
        description: 'We could not start the agent update. Try again.',
      });
    },
  });

  return (
    <DataSurface id="agent-status">
      <div className="border-b px-4 py-3">
        <h3 className="text-[13px] font-medium text-foreground">Agent status</h3>
      </div>
      <div className="px-4 py-3">
        {!configStatus && !needsPromptRefresh && (
          <div className="flex items-center justify-between gap-4">
            <p className="text-sm text-muted-foreground">
              Create an agent version from the current Agent Knowledge.
            </p>
            <Button
              onClick={() => generateMutation.mutate()}
              disabled={generateMutation.isPending || !canGenerate}
              size="sm"
            >
              {generateMutation.isPending ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <Plus className="mr-2 h-4 w-4" />
              )}
              Create Agent Version
            </Button>
          </div>
        )}

        {needsPromptRefresh && !isApplyingKnowledge && (
          <div className="flex items-center justify-between gap-4">
            <div className="space-y-1">
              <p className="text-sm">Agent Knowledge has updates ready for the live agent.</p>
              <p className="text-sm text-muted-foreground">
                {promptImpactSummary?.summary || 'Agent Knowledge changed after the last agent update.'}
              </p>
            </div>
            <Button
              onClick={() => generateMutation.mutate()}
              disabled={generateMutation.isPending || !canGenerate}
              size="sm"
            >
              {generateMutation.isPending ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <RotateCcw className="mr-2 h-4 w-4" />
              )}
              Update Agent
            </Button>
          </div>
        )}

        {isApplyingKnowledge && (
          <div className="flex items-center gap-3">
            <Loader2 className="h-5 w-5 animate-spin text-primary" />
            <p className="text-sm text-muted-foreground">
              Updating the agent with the latest Agent Knowledge...
            </p>
          </div>
        )}

        {configStatus === 'completed' && !needsPromptRefresh && !isApplyingKnowledge && (
          <div className="flex items-center gap-3">
            <CheckCircle2 className="h-5 w-5 text-green-500" />
            <p className="text-sm">Agent Knowledge is applied to your agent.</p>
          </div>
        )}

        {configStatus === 'failed' && (
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <AlertCircle className="h-5 w-5 text-destructive" />
              <p className="text-sm text-destructive">Agent update failed.</p>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={() => generateMutation.mutate()}
              disabled={generateMutation.isPending || !canGenerate}
            >
              <RotateCcw className="mr-2 h-4 w-4" />
              Retry
            </Button>
          </div>
        )}
      </div>
    </DataSurface>
  );
};

export const SessionsTab: React.FC<{
  sessions: InterviewerSessionSummary[];
  isLoading: boolean;
  selectedSessionId: string | null;
  onSelectSession: (id: string) => void;
  sessionDetail: SessionDetailResponse | null;
  sessionDetailLoading: boolean;
  onBackToList: () => void;
  interviewerId: string;
  interviewer?: InterviewerDetail;
  onCopyLink?: () => void;
}> = ({
  sessions,
  isLoading,
  selectedSessionId,
  onSelectSession,
  sessionDetail,
  sessionDetailLoading,
  onBackToList,
  interviewerId,
  interviewer,
  onCopyLink,
}) => {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [sessionToDelete, setSessionToDelete] = useState<InterviewerSessionSummary | null>(null);

  const deleteSessionMutation = useMutation({
    mutationFn: async (sessionId: string) => {
      const response = await onboardingService.deleteInterviewerSession(interviewerId, sessionId);
      if (!response.success) {
        throw new Error(response.error || 'Failed to delete session');
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['interviewer-sessions', interviewerId] });
      queryClient.invalidateQueries({ queryKey: ['interviewer', interviewerId] });
      toast({ title: 'Interview deleted' });
      setSessionToDelete(null);
    },
    onError: () => {
      toast({
        title: 'Failed to delete interview',
        description: 'We could not delete this interview. Try again.',
        variant: 'destructive',
      });
    },
  });

  const handleDeleteClick = (e: React.MouseEvent, session: InterviewerSessionSummary) => {
    e.stopPropagation();
    setSessionToDelete(session);
  };

  if (isLoading) {
    return (
      <DataSurface>
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </div>
      </DataSurface>
    );
  }

  if (sessions.length === 0) {
    const interviewLink = interviewer
      ? interviewer.interview_link || `${window.location.origin}/interview/${interviewer.token}`
      : null;
    return (
      <DataSurface className="border-dashed">
        <div className="flex flex-col items-center gap-3 px-4 py-12 text-center">
          <Users className="h-8 w-8 text-muted-foreground/60" />
          <div>
            <p className="text-sm font-medium text-foreground">No interviews yet</p>
            <p className="mx-auto mt-1 max-w-md text-sm text-muted-foreground">
              Share your interview link to capture buyer context that improves Agent Knowledge.
            </p>
          </div>
          {interviewLink ? (
            <div className="mt-1 flex flex-wrap items-center justify-center gap-2">
              <Button size="sm" onClick={() => window.open(interviewLink, '_blank')}>
                <ExternalLink className="mr-1.5 h-3.5 w-3.5" />
                Start interview
              </Button>
              {onCopyLink ? (
                <Button size="sm" variant="outline" onClick={onCopyLink}>
                  <Copy className="mr-1.5 h-3.5 w-3.5" />
                  Copy link
                </Button>
              ) : null}
            </div>
          ) : null}
        </div>
      </DataSurface>
    );
  }

  const canDelete = (phase: string) => phase !== 'completed';

  return (
    <>
      <DataSurface className="min-h-[520px]">
        <div className="grid min-h-[520px] lg:grid-cols-[380px_minmax(0,1fr)]">
          <aside className="border-b bg-muted/15 lg:border-b-0 lg:border-r">
            <div className="flex items-center justify-between gap-3 border-b px-4 py-3">
              <div className="flex items-center gap-1.5">
                <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Interviews</p>
                <HelpTooltip
                  text="Q&A sessions that shaped Agent Knowledge."
                  side="right"
                  className="ml-0 h-3 w-3"
                />
              </div>
              <Badge variant="outline">{sessions.length}</Badge>
            </div>
              <div className="divide-y">
                {sessions.map((session) => {
                  const phase = getPhaseLabel(session.phase);
                  const isSelected = session.id === selectedSessionId;

                  return (
                    <div
                      key={session.id}
                      role="button"
                      tabIndex={0}
                      onClick={() => onSelectSession(session.id)}
                      onKeyDown={(event) => {
                        if (event.key === 'Enter' || event.key === ' ') {
                          event.preventDefault();
                          onSelectSession(session.id);
                        }
                      }}
                      className={cn(
                        'cursor-pointer px-4 py-3 transition-colors hover:bg-background/60',
                        isSelected && 'bg-background'
                      )}
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0 flex-1">
                          <div className="flex min-w-0 items-center gap-2">
                            <User className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                            <span className="truncate text-sm font-medium text-foreground">
                              {session.interviewee_name || session.interviewee_email || 'Anonymous'}
                            </span>
                          </div>
                          <div className="mt-1.5 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-muted-foreground">
                            <Badge variant={phase.variant} className="h-5 shrink-0 px-1.5 text-[10px]">
                              {phase.label}
                            </Badge>
                            <span>
                              Topics covered {session.current_topic_index}/{session.total_topics}
                            </span>
                            <span>{new Date(session.created_at).toLocaleDateString()}</span>
                          </div>

                          {session.summary && (
                            <p className="mt-2 line-clamp-2 text-xs leading-5 text-muted-foreground">
                              {session.summary}
                            </p>
                          )}
                        </div>

                        <div className="flex shrink-0 items-center gap-1">
                          {canDelete(session.phase) && (
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-7 w-7 text-muted-foreground hover:text-destructive"
                              onClick={(e) => handleDeleteClick(e, session)}
                              aria-label={`Delete interview for ${session.interviewee_name || session.interviewee_email || 'Anonymous'}`}
                            >
                              <Trash2 className="h-3.5 w-3.5" />
                            </Button>
                          )}
                          <ChevronRight className="h-3.5 w-3.5 text-muted-foreground" />
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </aside>

            <div className="min-w-0">
              {selectedSessionId ? (
                <TranscriptTab
                  sessionDetail={sessionDetail}
                  isLoading={sessionDetailLoading}
                  onBack={onBackToList}
                  showBack={false}
                />
              ) : (
                <div className="flex h-full min-h-[320px] items-center justify-center px-4 py-12 text-sm text-muted-foreground">
                  Select an interview to view its transcript.
                </div>
              )}
            </div>
          </div>
        </DataSurface>

      <Dialog open={!!sessionToDelete} onOpenChange={(open) => { if (deleteSessionMutation.isPending) return; if (!open) setSessionToDelete(null); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Interview</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete the interview for{' '}
              <strong>
                {sessionToDelete?.interviewee_name || sessionToDelete?.interviewee_email || 'Anonymous'}
              </strong>
              ? This will remove the interview and all its transcript entries. This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setSessionToDelete(null)}
              disabled={deleteSessionMutation.isPending}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={() => sessionToDelete && deleteSessionMutation.mutate(sessionToDelete.id)}
              disabled={deleteSessionMutation.isPending}
            >
              {deleteSessionMutation.isPending ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Deleting...
                </>
              ) : (
                <>
                  <Trash2 className="mr-2 h-4 w-4" />
                  Delete Interview
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

const TranscriptTab: React.FC<{
  sessionDetail: SessionDetailResponse | null;
  isLoading: boolean;
  onBack: () => void;
  showBack?: boolean;
}> = ({ sessionDetail, isLoading, onBack, showBack = true }) => {
  const [activeDetailTab, setActiveDetailTab] = useState<'summary' | 'transcript'>('summary');

  useEffect(() => {
    setActiveDetailTab(sessionDetail?.summary ? 'summary' : 'transcript');
  }, [sessionDetail?.id, sessionDetail?.summary]);

  if (isLoading) {
    return (
      <div className="flex h-full min-h-[320px] items-center justify-center py-12">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!sessionDetail) {
    return (
      <div className="flex h-full min-h-[320px] items-center justify-center px-4 py-12">
        <div className="flex flex-col items-center gap-2 text-muted-foreground">
          <MessageSquare className="h-8 w-8 opacity-50" />
          <p className="text-sm">Select an interview to view its transcript</p>
        </div>
      </div>
    );
  }

  const entries = sessionDetail.entries || [];
  const phase = getPhaseLabel(sessionDetail.phase);
  const completedAt = sessionDetail.completed_at
    ? new Date(sessionDetail.completed_at).toLocaleDateString()
    : null;

  return (
    <Tabs
      value={activeDetailTab}
      onValueChange={(value) => setActiveDetailTab(value as 'summary' | 'transcript')}
      className="flex h-full min-h-[520px] min-w-0 flex-col"
    >
      <div className="border-b px-4 py-3">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="flex min-w-0 flex-wrap items-start gap-3">
            {showBack && (
              <Button variant="ghost" size="sm" onClick={onBack}>
                <ArrowLeft className="mr-1 h-4 w-4" />
                Back to Interviews
              </Button>
            )}
            <div className="min-w-0">
              <div className="flex min-w-0 flex-wrap items-center gap-2">
                <p className="truncate text-sm font-medium text-foreground">
                  {sessionDetail.interviewee_name || sessionDetail.interviewee_email || 'Anonymous'}
                </p>
                <Badge variant={phase.variant} className="h-5 px-1.5 text-[10px]">
                  {phase.label}
                </Badge>
              </div>
              <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-muted-foreground">
                <span>{entries.length} messages</span>
                <span>
                  Topics covered {sessionDetail.current_topic_index}/{sessionDetail.total_topics}
                </span>
                {completedAt && <span>Completed {completedAt}</span>}
              </div>
            </div>
          </div>
          <TabsList className="shrink-0">
            <TabsTrigger value="summary">Summary</TabsTrigger>
            <TabsTrigger value="transcript">Transcript</TabsTrigger>
          </TabsList>
        </div>
      </div>

      <TabsContent value="summary" className="min-h-0 flex-1 overflow-y-auto px-4 py-3">
        {sessionDetail.summary ? (
          <div className="prose prose-sm max-w-[78ch] dark:prose-invert">
            <Markdown content={sessionDetail.summary} className="text-sm leading-6" />
          </div>
        ) : (
          <div className="flex h-full min-h-[260px] items-center justify-center text-sm text-muted-foreground">
            No summary yet.
          </div>
        )}
      </TabsContent>

      <TabsContent value="transcript" className="min-h-0 flex-1 overflow-y-auto">
        <div className="divide-y divide-border/70">
          {entries.length === 0 ? (
            <p className="py-8 text-center text-sm text-muted-foreground">No messages</p>
          ) : (
            entries.map((entry: InterviewEntry) => (
              <div
                key={entry.id}
                className="grid items-start gap-2 px-3 py-2 transition-colors hover:bg-muted/20 sm:grid-cols-[44px_minmax(0,1fr)]"
              >
                <div className="pt-px text-[11px] font-medium leading-5 text-muted-foreground">
                  {entry.speaker === 'interviewer' ? 'Hobbes' : 'User'}
                </div>
                <div className="min-w-0">
                  {entry.speaker === 'interviewer' ? (
                    <div className="prose prose-sm max-w-[88ch] dark:prose-invert [&_p]:my-0 [&_p]:leading-5">
                      <Markdown content={entry.content} className="text-[13px]" />
                    </div>
                  ) : (
                    <p className="max-w-[88ch] whitespace-pre-wrap text-[13px] leading-5 text-foreground">
                      {entry.content}
                    </p>
                  )}
                </div>
              </div>
            ))
          )}
        </div>
      </TabsContent>
    </Tabs>
  );
};

export const AgentKnowledgeGapsTab: React.FC<{
  orgId: string;
  activeView: 'active' | 'analysis' | 'history';
}> = ({ orgId, activeView }) => {
  if (activeView === 'analysis') {
    return <TriggerPipelineTab orgId={orgId} />;
  }

  if (activeView === 'history') {
    return <RunHistoryTab orgId={orgId} />;
  }

  return <ActiveGapsTab orgId={orgId} />;
};

const InterviewerDetailPage: React.FC = () => {
  const { interviewerId } = useParams<{ interviewerId: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();
  const [selectedSessionId, setSelectedSessionId] = useState<string | null>(null);
  const requestedTab = searchParams.get('tab');
  const normalizedRequestedTab =
    requestedTab === 'sessions' || requestedTab === 'conversations' || requestedTab === 'coaching'
      ? 'interviews'
      : requestedTab;
  const initialTab = ['overview', 'knowledge', 'gaps', 'interviews', 'sources'].includes(normalizedRequestedTab || '')
    ? normalizedRequestedTab!
    : 'overview';
  const [activeTab, setActiveTab] = useState(initialTab);
  const [activeGapsView, setActiveGapsView] = useState<'active' | 'analysis' | 'history'>('active');
  const [generationPending, setGenerationPending] = useState(false);
  const [showAttachDialog, setShowAttachDialog] = useState(false);

  const {
    data: interviewerData,
    isLoading: interviewerLoading,
    error: interviewerError,
  } = useQuery({
    queryKey: ['interviewer', interviewerId],
    queryFn: async () => {
      if (!interviewerId) throw new Error('Interviewer ID required');
      const response = await onboardingService.getInterviewer(interviewerId);
      if (!response.success || !response.data) {
        throw new Error(response.error || 'Failed to fetch interviewer');
      }
      return response.data;
    },
    enabled: !!interviewerId,
    retry: false,
    refetchInterval: (query) => {
      const data = query.state.data;
      const latestBatch = data?.ingestion_batches?.[0];
      const shouldPollIngestion =
        !!data &&
        (['processing', 'planning'].includes(data.processing_status) ||
          (!!latestBatch && !['completed', 'failed'].includes(latestBatch.status)));
      const shouldPollPromptRefresh =
        !!data && (
          data.prompt_status === 'regenerating'
          || data.config_generation_status === 'generating'
          || generationPending
        );

      if (shouldPollPromptRefresh) return 3000;
      if (shouldPollIngestion) return 5000;
      return false;
    },
  });

  useEffect(() => {
    const promptRefreshActive =
      interviewerData?.config_generation_status === 'generating'
      || interviewerData?.prompt_status === 'regenerating';
    if (!promptRefreshActive) {
      setGenerationPending(false);
    }
  }, [interviewerData?.config_generation_status, interviewerData?.prompt_status]);

  const {
    data: sessionsData,
    isLoading: sessionsLoading,
  } = useQuery({
    queryKey: ['interviewer-sessions', interviewerId],
    queryFn: async () => {
      if (!interviewerId) throw new Error('Interviewer ID required');
      const response = await onboardingService.listInterviewerSessions(interviewerId);
      if (!response.success || !response.data) {
        throw new Error(response.error || 'Failed to fetch interviews');
      }
      return response.data;
    },
    enabled: !!interviewerId,
    retry: false,
  });

  const {
    data: sessionDetail,
    isLoading: sessionDetailLoading,
  } = useQuery({
    queryKey: ['interviewer-session-detail', interviewerId, selectedSessionId],
    queryFn: async () => {
      if (!interviewerId || !selectedSessionId) throw new Error('IDs required');
      const response = await onboardingService.getInterviewerSessionDetail(
        interviewerId,
        selectedSessionId
      );
      if (!response.success || !response.data) {
        throw new Error(response.error || 'Failed to fetch session detail');
      }
      return response.data;
    },
    enabled: !!interviewerId && !!selectedSessionId,
    retry: false,
  });

  const interviewer = interviewerData;
  const sessions = sessionsData?.sessions || [];
  const { reingestInterviewer, isReingesting } = useInterviewerSetup(interviewer?.organization_id ?? '');
  const { data: gapReport } = useQuery({
    queryKey: ['knowledge-gaps-report', interviewer?.organization_id],
    queryFn: async () => {
      if (!interviewer?.organization_id) return null;
      const response = await knowledgeGapsService.getReport(interviewer.organization_id);
      return response.success && response.data ? response.data : null;
    },
    enabled: !!interviewer?.organization_id,
    refetchInterval: 60_000,
    staleTime: 30_000,
  });
  const activeGapCount = gapReport?.total_active_summaries ?? 0;

  useEffect(() => {
    setActiveTab(initialTab);
  }, [initialTab]);

  const handleTabChange = (value: string) => {
    setActiveTab(value);
    if (value === 'overview') {
      setSearchParams({}, { replace: true });
    } else {
      setSearchParams({ tab: value }, { replace: true });
    }
  };

  const handleSelectSession = (sessionId: string) => {
    setSelectedSessionId(sessionId);
  };

  const handleBackToSessions = () => {
    setSelectedSessionId(null);
  };

  const handleRefreshSources = async () => {
    if (!interviewer || !interviewerId) return;
    const result = await reingestInterviewer(interviewer.id, 'full_rebuild');
    if (result) {
      queryClient.invalidateQueries({ queryKey: ['interviewer', interviewerId] });
      queryClient.invalidateQueries({ queryKey: ['interviewers', interviewer.organization_id] });
    }
  };

  if (interviewerLoading) {
    return (
      <>
        <Helmet>
          <title>Agent Knowledge | Hobbes</title>
        </Helmet>
        <div className="flex h-64 items-center justify-center">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
      </>
    );
  }

  if (interviewerError || !interviewer) {
    return (
      <>
        <Helmet>
          <title>Agent Knowledge | Hobbes</title>
        </Helmet>
        <div className="px-4 pt-2 sm:px-6 md:px-8">
          <PageHeader
            breadcrumbs={[
              { label: 'Agent Knowledge', href: '/agent-knowledge' },
              { label: 'Details' },
            ]}
            titleTooltip="Review what your agent knows and what still needs attention."
          />
          <Alert variant="destructive" className="mt-4">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>
              {interviewerError instanceof Error
                ? interviewerError.message
                : 'Failed to load Agent Knowledge details'}
            </AlertDescription>
          </Alert>
        </div>
      </>
    );
  }

  return (
    <>
      <Helmet>
        <title>{interviewer.name || 'Agent Knowledge'} | Hobbes</title>
      </Helmet>

      <div className="flex min-h-0 flex-col">
        <PageHeader
          breadcrumbs={[
            { label: 'Agent Knowledge', href: '/agent-knowledge' },
            { label: interviewer.name || 'Agent Knowledge', icon: GraduationCap },
          ]}
          titleTooltip="Review what your agent knows and what still needs attention."
        />

        <div className="flex-1 px-4 pb-8 sm:px-6 md:px-8">
          <Tabs value={activeTab} onValueChange={handleTabChange}>
            <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
              <TabsList className="-ml-3 flex h-auto flex-wrap justify-start gap-2 bg-transparent p-0">
                <TabsTrigger value="overview">
                  <LayoutList className="mr-1.5 h-4 w-4" />
                  Overview
                </TabsTrigger>
                <TabsTrigger value="knowledge">
                  <GraduationCap className="mr-1.5 h-4 w-4" />
                  Knowledge
                </TabsTrigger>
                <TabsTrigger value="gaps" className="gap-1.5">
                  <LayoutList className="mr-1.5 h-4 w-4" />
                  Gaps
                  {activeGapCount > 0 && (
                    <Badge variant="secondary" className="h-4 px-1.5 text-[10px]">
                      {activeGapCount}
                    </Badge>
                  )}
                </TabsTrigger>
                <TabsTrigger value="interviews">
                  <Users className="mr-1.5 h-4 w-4" />
                  Interviews ({sessions.length})
                </TabsTrigger>
                <TabsTrigger value="sources">
                  <Archive className="mr-1.5 h-4 w-4" />
                  Sources
                </TabsTrigger>
              </TabsList>

              {activeTab === 'gaps' && (
                <div className="inline-flex rounded-md bg-muted p-1 text-muted-foreground">
                  {[
                    ['active', 'Open Gaps'],
                    ['analysis', 'Find Gaps'],
                    ['history', 'History'],
                  ].map(([value, label]) => (
                    <button
                      key={value}
                      type="button"
                      onClick={() => setActiveGapsView(value as typeof activeGapsView)}
                      className={cn(
                        'inline-flex h-8 items-center justify-center whitespace-nowrap rounded-sm px-3 text-sm font-medium transition-colors',
                        activeGapsView === value
                          ? 'bg-background text-foreground shadow-sm'
                          : 'hover:text-foreground'
                      )}
                    >
                      {label}
                    </button>
                  ))}
                </div>
              )}

              {activeTab === 'sources' && (
                <div className="flex flex-wrap gap-2">
                  <Button size="sm" onClick={() => setShowAttachDialog(true)}>
                    <Plus className="mr-1.5 h-3.5 w-3.5" />
                    Add Sources
                  </Button>
                  <Button size="sm" variant="outline" onClick={handleRefreshSources} disabled={isReingesting}>
                    {isReingesting ? (
                      <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />
                    ) : (
                      <RotateCcw className="mr-1.5 h-3.5 w-3.5" />
                    )}
                    Refresh Sources
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => navigate('/source-library')}>
                    <Archive className="mr-1.5 h-3.5 w-3.5" />
                    Source Library
                  </Button>
                </div>
              )}
            </div>

            <TabsContent value="overview">
              <OverviewTab
                interviewer={interviewer}
                interviewerId={interviewerId!}
                activeGapCount={activeGapCount}
                onGenerationStarted={() => setGenerationPending(true)}
              />
            </TabsContent>

            <TabsContent value="knowledge">
              <KnowledgeTab interviewer={interviewer} activeGapCount={activeGapCount} />
            </TabsContent>

            <TabsContent value="gaps">
              <AgentKnowledgeGapsTab orgId={interviewer.organization_id} activeView={activeGapsView} />
            </TabsContent>

            <TabsContent value="interviews">
              <SessionsTab
                sessions={sessions}
                isLoading={sessionsLoading}
                selectedSessionId={selectedSessionId}
                onSelectSession={handleSelectSession}
                sessionDetail={sessionDetail || null}
                sessionDetailLoading={sessionDetailLoading}
                onBackToList={handleBackToSessions}
                interviewerId={interviewerId!}
              />
            </TabsContent>

            <TabsContent value="sources">
              <SourcesTab
                interviewer={interviewer}
                interviewerId={interviewerId!}
                onAddSources={() => setShowAttachDialog(true)}
              />
            </TabsContent>
          </Tabs>

          <AttachKnowledgeDialog
            interviewer={interviewer}
            open={showAttachDialog}
            onOpenChange={setShowAttachDialog}
          />
        </div>
      </div>
    </>
  );
};

export default InterviewerDetailPage;
